import Tabs from "common/Tabs/Tabs";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { SignalsBotSummaryTab } from "@shared/features/bots/types";
import SignalsSummary from "features/bots/BotForm/Summaries/SignalsSummary";
import BacktestingHistory from "features/bots/BotForm/Backtesting/BacktestingHistory";
import styled, { css, useTheme } from "styled-components";
import { panelTabs } from "features/bots/BotForm/NewLook/styles";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import SeeAllBacktestingsBanner from "features/bots/BotForm/Backtesting/common/SeeAllBacktestingsBanner";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

interface SignalsBotSummaryTabsProps {
  defaultTab?: SignalsBotSummaryTab;
  height?: number;
}

const SignalsBotSummaryTabs = ({
  defaultTab,
  height,
}: SignalsBotSummaryTabsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  const [tab, setTab] = useState(SignalsBotSummaryTab.MOST_TRADED_PAIR);
  const { botType, isBacktesting, showBacktesting } =
    useContext(BotFormContext)!;

  useEffect(() => {
    if (defaultTab) setTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (isBacktesting && tab !== SignalsBotSummaryTab.BACKTESTING_HISTORY) {
      setTab(SignalsBotSummaryTab.BACKTESTING_HISTORY);
    }
  }, [isBacktesting]);

  const mostTradedPairContent = useMemo(() => {
    return (
      <div
        style={{
          padding: isNewLook
            ? "16px 20px 20px"
            : showBacktesting
            ? 16
            : "0 16px 16px",
        }}
      >
        <SeeAllBacktestingsBanner
          botType={botType}
          selectedItems={["group", "exchange"]}
          hideSeeAllButton={true}
          style={{ paddingBottom: 16 }}
        />
        <SignalsSummary hideTitle={true} />
      </div>
    );
  }, [botType, isNewLook]);

  const backtestingHistoryContent = useMemo(() => {
    return (
      <BacktestingHistory
        wrapperStyle={{
          maxHeight: 700,
          minHeight: Math.max(height || 0 - TABS_HEIGHT, 250),
          paddingBottom: 16,
        }}
      />
    );
  }, [height]);

  const tabsItems = useMemo(
    () => [
      {
        key: SignalsBotSummaryTab.MOST_TRADED_PAIR,
        label: t(SignalsBotSummaryTab.MOST_TRADED_PAIR),
        children: mostTradedPairContent,
      },
      {
        key: SignalsBotSummaryTab.BACKTESTING_HISTORY,
        label: t(SignalsBotSummaryTab.BACKTESTING_HISTORY),
        children: backtestingHistoryContent,
      },
    ],
    [mostTradedPairContent, backtestingHistoryContent]
  );

  if (!showBacktesting) {
    return mostTradedPairContent;
  }

  return (
    <StyledTabs
      activeKey={tab}
      onChange={(tab: string) => setTab(tab as SignalsBotSummaryTab)}
      items={tabsItems}
    />
  );
};

export default SignalsBotSummaryTabs;

const TABS_HEIGHT = 34;

const StyledTabs = styled(Tabs)`
  &.ant-tabs .ant-tabs-tab {
    padding: 0 0 12px;
  }
  &.ant-tabs-top > .ant-tabs-nav {
    padding: 0 12px;
    margin: 0 !important;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${panelTabs};
      &.ant-tabs-top > .ant-tabs-nav {
        padding: 0;
      }
    `};
`;
